import React from 'react';
import NeatScheduleMark from '../../shared/components/brand/NeatScheduleMark';
import './MaintenancePage.css';

/**
 * Format ISO date in user's local time with timezone (e.g. "Jan 30, 3:00 PM CST (your local time)").
 */
function formatScheduledTimeWithZone(isoString) {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return null;
    const formatted = d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    let timeZoneName = '';
    try {
      const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(d);
      const tz = parts.find((p) => p.type === 'timeZoneName');
      if (tz) timeZoneName = tz.value;
    } catch {
      // ignore
    }
    return timeZoneName ? `${formatted} ${timeZoneName} (your local time)` : `${formatted} (your local time)`;
  } catch {
    return null;
  }
}

/**
 * Full-screen maintenance page with logo and professional layout.
 * @param {'app' | 'website'} variant - "app" for scheduler app maintenance, "website" for marketing site maintenance
 * @param {string | null} [scheduledStartTime] - ISO date string for when maintenance was scheduled (app variant only)
 */
const MaintenancePage = ({ variant = 'app', scheduledStartTime = null }) => {
  const isWebsite = variant === 'website';
  const title = isWebsite ? 'Website Maintenance' : 'System Maintenance';
  const message = isWebsite
    ? 'Our website is currently undergoing maintenance. We\'ll be back shortly.'
    : 'We\'re currently performing system maintenance. Please check back later.';
  const formattedTime = !isWebsite && scheduledStartTime ? formatScheduledTimeWithZone(scheduledStartTime) : null;

  return (
    <div className="maintenance-page" role="main" aria-live="polite">
      <div className="maintenance-page__inner">
        <div className="maintenance-page__animation" aria-hidden="true">
          <video
            src={`${process.env.PUBLIC_URL || ''}/maint.webm`}
            autoPlay
            loop
            muted
            playsInline
            className="maintenance-page__video"
            aria-hidden="true"
          />
        </div>
        <h1 className="maintenance-page__title">{title}</h1>
        <p className="maintenance-page__message">{message}</p>
        {formattedTime && (
          <p className="maintenance-page__scheduled">Scheduled maintenance at {formattedTime}</p>
        )}
        <NeatScheduleMark variant="dark" className="maintenance-page__logo" />
      </div>
    </div>
  );
};

export default MaintenancePage;
